import './assets/fonts/fonts.css';

import React, { Suspense, useEffect, useMemo } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorState, LoadingIndicator } from '@manulife-web/shared-module';
import { HealthProfileRoutes } from '@leagueplatform/health-profile';
import { CareCollectionsRoutes } from '@leagueplatform/care-collections';
import { CareDiscoveryRoutes } from '@leagueplatform/care-discovery';
import { RewardsEventController } from '@leagueplatform/rewards-events';
import League, {
  getModulePath,
  LEAGUE_MODULE_NAMES,
  LeagueConfig,
  overwriteRoutePaths,
} from '@leagueplatform/core';
import {
  initManulifeWebExtensionsSDK,
  REWARDS_HUB_ROUTE,
  RewardsHubRoutes,
  RewardsPointsRefreshListener,
} from '@leagueplatform/manulife-web-extensions';
import { useAuth0 } from '@auth0/auth0-react';
import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import {
  HealthJourneyRoutes,
  ProgramOverviewHistoryPage,
  ProgramOverviewPage,
} from '@leagueplatform/health-journey';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ScrollToTop } from 'utils/scrollToTop';
import { AssessmentRoutes } from '@leagueplatform/assessment';
import { MasonryDriverActionController } from '@leagueplatform/masonry';
import { DEFAULT_NAMESPACE } from '@leagueplatform/masonry-engine';
import { Away } from './pages/Away';
import { BasePage } from './components/BasePage';
import Home from './pages/Home';
import { getConfig } from './config';
import { MasonryContainerView } from './pages/MasonryContainer.view';
// DEV ONLY — harness for testing `useRefreshRewardsAfterUnlink` (MANU-5312).
// See the hook's JSDoc for the full test recipe.
import {
  DevUnlinkModal,
  DEV_UNLINK_MODAL_EVENT,
} from './components/DevUnlinkModal';

initManulifeWebExtensionsSDK();

MasonryDriverActionController.registerHandler(
  'triggerUnlinkAeroplanModal',
  DEFAULT_NAMESPACE,
  ({ payload }) => {
    // eslint-disable-next-line no-console
    console.log('triggerUnlinkAeroplanModal');
    // eslint-disable-next-line no-console
    console.log(payload);
    // your code here to trigger the modal
    // DEV ONLY — open <DevUnlinkModal /> so we can test the refresh hook.
    window.dispatchEvent(new CustomEvent(DEV_UNLINK_MODAL_EVENT));
  },
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
    },
  },
});

export const AuthViewLogout = () => {
  const { logout } = useAuth0();
  React.useEffect(() => {
    logout({
      returnTo: window.location.origin,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingIndicator />;
};

export const AuthViewSignIn = () => {
  const { loginWithRedirect } = useAuth0();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        maxWidth: '320px',
        flexDirection: 'column',
        gap: '12px',
        margin: '32px auto',
      }}
    >
      Log into Manulife to continue
      <button
        onClick={() => loginWithRedirect()}
        style={{
          cursor: 'pointer',
        }}
      >
        Login
      </button>
    </div>
  );
};

function useTheme(defaultTheme = 'manulife_light') {
  useEffect(() => {
    let activeTheme = localStorage.getItem('MANULIFE_ACTIVE_THEME');
    if (!activeTheme) {
      activeTheme = defaultTheme;
      localStorage.setItem('MANULIFE_ACTIVE_THEME', activeTheme);
    }
  }, [defaultTheme]);
}

function Loading() {
  return <p>loading...</p>;
}

const LeagueProviderWrapper = () => {
  const { getAccessTokenSilently } = useAuth0();
  const config: LeagueConfig = useMemo(
    () =>
      getConfig(
        getAccessTokenSilently,
        '343', // hardcoded sponsor id for rewards hub for staging
      ),
    [getAccessTokenSilently],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <League.LeagueProvider config={config}>
        <ErrorBoundary fallback={<ErrorState />}>
          <Suspense fallback={<LoadingIndicator />}>
            <RewardsEventController />
            <RewardsPointsRefreshListener />
            <Outlet />
            <ReactQueryDevtools />
            {/* DEV ONLY — harness for useRefreshRewardsAfterUnlink (MANU-5312). */}
            <DevUnlinkModal />
          </Suspense>
        </ErrorBoundary>
      </League.LeagueProvider>
    </QueryClientProvider>
  );
};

const Layout = () => {
  const { isLoading, error, user, isAuthenticated } = useAuth0();

  useTheme();

  if (isLoading) {
    return (
      <div>
        <p>loading...</p>
      </div>
    );
  }

  if (!user || !isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  if (error) {
    return <div>Oops... {error.message}</div>;
  }

  return (
    <>
      <ScrollToTop />
      <LeagueProviderWrapper />
    </>
  );
};

// Mimic nested routes like Manulife's app
overwriteRoutePaths({
  [LEAGUE_MODULE_NAMES.careDiscovery]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.careDiscovery,
  )}`,
  [LEAGUE_MODULE_NAMES.healthJourney]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.healthJourney,
  )}`,
  [LEAGUE_MODULE_NAMES.healthPrograms]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.healthPrograms,
  )}`,
  [LEAGUE_MODULE_NAMES.healthProgress]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.healthProgress,
  )}`,
  [LEAGUE_MODULE_NAMES.healthJourneyHistory]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.healthJourneyHistory,
  )}`,
  [LEAGUE_MODULE_NAMES.careLibrary]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.careLibrary,
  )}`,
  [LEAGUE_MODULE_NAMES.careCollections]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.careCollections,
  )}`,
  [LEAGUE_MODULE_NAMES.program]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.program,
  )}`,
  [LEAGUE_MODULE_NAMES.challenge]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.challenge,
  )}`,
  [LEAGUE_MODULE_NAMES.assessment]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.assessment,
  )}`,
  [LEAGUE_MODULE_NAMES.healthProfile]: `/gb/member-portal/secured/member${getModulePath(
    LEAGUE_MODULE_NAMES.healthProfile,
  )}`,
});

const router = createBrowserRouter([
  {
    path: '/away',
    element: <Away />,
    loader: Loading,
  },
  {
    path: '/',
    element: <Layout />,
    loader: Loading,
    children: [
      {
        path: '/',
        element: <BasePage />,
        children: [
          { index: true, element: <Home /> },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careDiscovery)}/*`,
            element: <CareDiscoveryRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careCollections)}/*`,
            element: <CareCollectionsRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.healthJourney)}/*`,
            element: <HealthJourneyRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.healthPrograms)}/*`,
            element: <HealthJourneyRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProfile)}/*`,
            element: <HealthProfileRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.healthProgress)}/*`,
            element: <HealthJourneyRoutes />,
          },
          {
            path: `${getModulePath(
              LEAGUE_MODULE_NAMES.healthJourneyHistory,
            )}/*`,
            element: <HealthJourneyRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.careLibrary)}/*`,
            element: <HealthJourneyRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.program)}/*`,
            element: <ProgramOverviewPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.challenge)}/*`,
            element: <ProgramOverviewPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.program)}/history/*`,
            element: <ProgramOverviewHistoryPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.challenge)}/history/*`,
            element: <ProgramOverviewHistoryPage />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.assessment)}/:moduleId`,
            element: <AssessmentRoutes />,
          },
          {
            path: `${getModulePath(LEAGUE_MODULE_NAMES.assessment)}/*`,
            element: <AssessmentRoutes />,
          },
          {
            path: `/gb/member-portal/secured/member${REWARDS_HUB_ROUTE}/*`,
            element: <RewardsHubRoutes />,
          },
          {
            path: `/masonry-container-view`,
            element: <MasonryContainerView />,
          },
        ],
      },
      {
        path: '/sign-out',
        element: <AuthViewLogout />,
      },
    ],
  },
  {
    path: '/sign-in',
    element: <AuthViewSignIn />,
  },
]);

const App = () => <RouterProvider router={router} />;

export default App;
